import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Users, Sparkles, Trash2, Pencil } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { StatusBadge, EmptyState } from "@/components/Primitives";
import { RpSelect } from "@/components/RpSelect";
import { StaggerRoot, StaggerItem } from "@/components/motion";
import { useOperationalSync } from "@/hooks/useOperationalSync";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const SECTIONS = ["سالن اصلی", "خانواده", "VIP", "تراس"];

export default function Tables() {
  const { t, money, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [orders, setOrders] = useState([]);
  const [section, setSection] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ number: "", capacity: 4, section: SECTIONS[0], status: "available" });
  const [edit, setEdit] = useState(null);

  const [view, setView] = useState("all"); // all | mine | available | occupied

  const load = useCallback(async () => {
    const params = { limit: 500 };
    if (user?.role === "waiter" && view === "mine") {
      params.my_tables = true;
    }
    const [t1, t2] = await Promise.all([
      api.get("/tables", { params }),
      api.get("/orders", { params: { payment_status: "unpaid", limit: 300 } }),
    ]);
    setTables(t1.data);
    setOrders(t2.data);
  }, [user?.role, view]);

  useOperationalSync(load, { pollMs: 8000 });
  useEffect(() => { load(); }, [load]);

  const orderByTable = orders.reduce((acc, o) => {
    if (!o.table_id) return acc;
    const prev = acc[o.table_id];
    if (!prev || new Date(o.created_at) > new Date(prev.created_at)) {
      acc[o.table_id] = o;
    }
    return acc;
  }, {});

  const canManage = ["owner", "manager"].includes(user?.role);

  const filtered = tables
    .filter((tbl) => (section === "all" ? true : tbl.section === section))
    .filter((tbl) => (search ? String(tbl.number).includes(search) : true))
    .filter((tbl) => {
      if (view === "available") return tbl.status === "available";
      if (view === "occupied") return tbl.status === "occupied";
      if (view === "mine" && user?.role === "waiter") {
        return tbl.assigned_waiter_id === user.id;
      }
      return true;
    });

  const save = async () => {
    try {
      if (edit) {
        await api.put(`/tables/${edit.id}`, { ...form, number: Number(form.number), capacity: Number(form.capacity) });
        toast.success(t("success"));
      } else {
        await api.post("/tables", { ...form, number: Number(form.number), capacity: Number(form.capacity) });
        toast.success(t("success"));
      }
      setOpen(false);
      setEdit(null);
      setForm({ number: "", capacity: 4, section: SECTIONS[0], status: "available" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const del = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/tables/${id}`);
      toast.success(t("success"));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const markClean = async (id) => {
    try {
      await api.put(`/tables/${id}/status`, { status: "available" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openOrderFor = (tbl) => {
    const ord = orderByTable[tbl.id];
    if (ord) {
      navigate(`/new-order/${ord.id}`);
    } else {
      navigate(`/new-order?tableId=${tbl.id}`);
    }
  };

  return (
    <StaggerRoot className="space-y-6">
      <StaggerItem index={0}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-heading">{t("tables")}</h1>
          <p className="text-sm text-gray-500 mt-1">{tables.length} {lang === "pashto" ? "مېزونه" : "میز"}</p>
        </div>
        {canManage && (
          <button
            data-testid="add-table"
            onClick={() => { setEdit(null); setForm({ number: "", capacity: 4, section: SECTIONS[0], status: "available" }); setOpen(true); }}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#071A33] text-white text-sm font-medium hover:bg-[#7F1020]"
          >
            <Plus className="w-4 h-4" /> {t("addTable")}
          </button>
        )}
      </div>
      </StaggerItem>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSection("all")}
          data-testid="section-all"
          className={`px-4 py-2 rounded-xl text-sm font-medium border ${section === "all" ? "bg-[#071A33] text-white border-[#071A33]" : "bg-white text-gray-700 border-gray-200"}`}
        >
          {t("allSections")}
        </button>
        {SECTIONS.map((s) => (
          <button
            key={s}
            onClick={() => setSection(s)}
            className={`px-4 py-2 rounded-xl text-sm font-medium border ${section === s ? "bg-[#071A33] text-white border-[#071A33]" : "bg-white text-gray-700 border-gray-200"}`}
          >
            {s}
          </button>
        ))}
        {user?.role === "waiter" && (
          <>
            {["all", "mine", "available", "occupied"].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={`px-4 py-2 rounded-xl text-sm font-medium border ${view === v ? "bg-[#7F1020] text-white border-[#7F1020]" : "bg-white text-gray-700 border-gray-200"}`}
              >
                {v === "mine" ? (lang === "pashto" ? "زما میزونه" : "میزهای من") : t(v) || v}
              </button>
            ))}
          </>
        )}
        <input
          data-testid="table-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("search") + " " + t("tableNumber")}
          className="flex-1 min-w-[180px] px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm focus:border-[#071A33] focus:outline-none focus:ring-2 focus:ring-[#071A33]/20"
        />
      </div>

      {!filtered.length ? (
        <EmptyState icon={Users} title={t("empty")} />
      ) : (
        <StaggerRoot className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" delay={0.03}>
          {filtered.map((tbl, idx) => {
            const ord = orderByTable[tbl.id];
            return (
              <StaggerItem key={tbl.id} index={idx}>
              <div data-testid={`table-card-${tbl.number}`} className="rp-card rp-card-hover p-5 flex flex-col gap-3 h-full">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs text-gray-500">{tbl.section}</div>
                    <div className="text-3xl font-bold font-heading text-gray-900 tabular mt-0.5">
                      {tbl.number}
                    </div>
                  </div>
                  <StatusBadge status={tbl.status} label={t(tbl.status)} />
                </div>

                <div className="flex items-center gap-1.5 text-sm text-gray-500">
                  <Users className="w-4 h-4" />
                  <span>{tbl.capacity}</span>
                  {ord && (
                    <>
                      <span className="mx-1">•</span>
                      <span className="tabular font-medium text-gray-900">{money(ord.total || ord.subtotal)}</span>
                    </>
                  )}
                </div>

                <div className="flex flex-col gap-2 mt-1">
                  <button
                    data-testid={`table-order-${tbl.number}`}
                    onClick={() => openOrderFor(tbl)}
                    className="px-3 py-2 rounded-xl bg-[#071A33] hover:bg-[#7F1020] text-white text-sm font-medium"
                  >
                    {ord ? t("openOrder") : t("startOrder")}
                  </button>
                  <div className="flex gap-2">
                    {tbl.status === "cleaning" && (
                      <button onClick={() => markClean(tbl.id)} className="flex-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-700 hover:bg-gray-50 inline-flex items-center justify-center gap-1">
                        <Sparkles className="w-3.5 h-3.5" /> {t("markClean")}
                      </button>
                    )}
                    {canManage && (
                      <>
                        <button
                          onClick={() => { setEdit(tbl); setForm({ number: tbl.number, capacity: tbl.capacity, section: tbl.section, status: tbl.status }); setOpen(true); }}
                          className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => del(tbl.id)} className="px-2.5 py-1.5 rounded-lg border border-rose-100 text-rose-600 hover:bg-rose-50">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
              </StaggerItem>
            );
          })}
        </StaggerRoot>
      )}

      {/* Add/Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{edit ? t("edit") : t("addTable")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-gray-600 mb-1 block">{t("tableNumber")}</label>
              <input data-testid="form-table-number" type="number" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className="w-full h-11 px-4 rounded-xl border border-gray-200 focus:outline-none focus:border-[#071A33]" />
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1 block">{t("capacity")}</label>
              <input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="w-full h-11 px-4 rounded-xl border border-gray-200 focus:outline-none focus:border-[#071A33]" />
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1 block">{t("section")}</label>
              <RpSelect
                value={form.section}
                onValueChange={(v) => setForm({ ...form, section: v })}
                options={SECTIONS.map((s) => ({ value: s, label: s }))}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 flex-row-reverse sm:flex-row-reverse">
            <button data-testid="save-table" onClick={save} className="px-5 py-2.5 rounded-xl bg-[#071A33] text-white text-sm font-medium hover:bg-[#7F1020]">{t("save")}</button>
            <button onClick={() => setOpen(false)} className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm">{t("cancel")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StaggerRoot>
  );
}
